import React from 'react';
import Drawer from '@mui/material/Drawer';
import {FLUX_DRAWER_WIDTH, WHITE_100} from '../../theme';
import PropTypes from 'prop-types';
import {DrawerHeader} from '../common/DrawerHeader';
import {Button, Skeleton, Stack, Typography} from '@mui/material';
import {BlendIcon, RedoDotIcon, SquarePenIcon} from 'lucide-react';
import {
  useEditFluxAnalysisMutation,
  usePatchFluxAnalysisMutation,
} from '../../data/copilotV2Api';

const styles = {
  drawer: {
    marginTop: '3rem',
    width: FLUX_DRAWER_WIDTH,
    background: WHITE_100,
    borderLeft: '1px solid #DDE0DE',
    boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.10)',
    height: `calc(100vh - 3rem)`,
  },
  button: {
    justifyContent: 'left',
    color: '#032321',
    fontWeight: 500,
    fontSize: 12,
    borderRadius: '9px',
    gap: '8px',
    '&:hover': {
      backgroundColor: '#F2F4F4',
    },
  },
  acceptButton: {
    color: '#34A290',
    fontSize: 12,
    borderRadius: '8px',
    padding: '6px 12px',
    border: '1px solid #CDD3D3',
    width: '100%',
  },
  rejectButton: {
    color: '#E85835',
    fontSize: 12,
    borderRadius: '8px',
    padding: '6px 12px',
    border: '1px solid #CDD3D3',
    width: '100%',
  },
};

export const FluxDrawer = React.memo(
  ({open, onClose, fluxData, reportType, period, comparePeriod, onPatch}) => {
    const [editFlux, {isLoading, data, isError, reset}] =
      useEditFluxAnalysisMutation();
    const [patchAnalysis] = usePatchFluxAnalysisMutation();

    // data.updated_analysis
    //  -> on Accept, call usePatchFluxAnalysisMutation to update data in API
    //  -> on Reject, reset useEditFluxAnalysisMutation
    // /api/v1/flux-analysis/{report_type}/{period}/{compare_period}/{pli_id}
    // Updates the analysis text for a specific presentation line item

    async function handleClick(editType) {
      try {
        await editFlux({
          period,
          comparePeriod,
          currentAnalysis: fluxData.analysis,
          reportType,
          editType,
          pliId: fluxData.pliId,
        });
      } catch (e) {
        console.error(e);
        // toast?
      }
    }
    const handleClose = (_event, _reason) => {
      if (isLoading || data || isError) {
        if (
          confirm(
            'Explanation edit in progress. Are you sure you want to leave? Changes will be lost.'
          )
        ) {
          reset();
          onClose();
        }
      } else {
        onClose();
      }
    };

    async function handleAccept() {
      if (!data?.updated_analysis) {
        return;
      }

      try {
        const result = await patchAnalysis({
          period,
          comparePeriod,
          analysis: data?.updated_analysis,
          reportType,
          pliId: fluxData.pliId,
        });

        onPatch({pliId: fluxData.pliId, analysis: result.data.analysis});
        reset();
      } catch (e) {
        console.error(e);
      }
    }

    function getAnalysisText() {
      if (data?.updated_analysis) {
        return data?.updated_analysis;
      }
      if (fluxData?.analysis) {
        return fluxData?.analysis;
      }
      return "An explanation wasn't found.";
    }

    return (
      <Drawer
        anchor={'right'}
        open={open}
        variant="temporary"
        onClose={handleClose}
        sx={{zIndex: 998}}
        PaperProps={{sx: styles.drawer}}
      >
        {isLoading ? (
          <Stack p={3} gap={2}>
            {Array.from({length: 5}).map((index) => (
              <Stack key={index}>
                <Skeleton width={133} />
                <Skeleton width={103} />
                <Skeleton width={157} />
                <Skeleton width={89} />
              </Stack>
            ))}
          </Stack>
        ) : (
          <>
            <DrawerHeader
              onClose={handleClose}
              sx={{borderBottom: '1px solid #E1E5E4'}}
            >
              <Typography fontSize={14} fontWeight={500}>
                Explanation
              </Typography>
            </DrawerHeader>
            <Stack>
              <Typography p={3}>{getAnalysisText()}</Typography>
            </Stack>
          </>
        )}
        <Stack
          p={3}
          position="sticky"
          bottom={0}
          sx={{backgroundColor: 'white'}}
          borderTop="1px solid #E1E5E4"
        >
          <Typography fontWeight="bold" fontSize={12} pb={2} pl={1}>
            Edit with AI
          </Typography>
          {data?.updated_analysis ? (
            <Stack direction="row" gap={2}>
              <Button sx={styles.acceptButton} onClick={handleAccept}>
                Accept
              </Button>
              <Button sx={styles.rejectButton} onClick={() => reset()}>
                Reject
              </Button>
            </Stack>
          ) : (
            <>
              <Button
                sx={styles.button}
                disabled={isLoading}
                onClick={async () => {
                  await handleClick('condense');
                }}
              >
                <BlendIcon size={20} color="#162523" />
                Condense this
              </Button>
              <Button
                sx={styles.button}
                disabled={isLoading}
                onClick={async () => {
                  await handleClick('try_again');
                }}
              >
                <RedoDotIcon size={20} color="#162523" /> Try again
              </Button>
              <Button
                sx={styles.button}
                disabled={isLoading}
                onClick={async () => {
                  await handleClick('add_more_detail');
                }}
              >
                <SquarePenIcon size={20} color="#162523" /> Add more details
              </Button>
            </>
          )}
        </Stack>
      </Drawer>
    );
  }
);

FluxDrawer.displayName = 'FluxDrawer';

FluxDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  fluxData: PropTypes.object,
  onPatch: PropTypes.func.isRequired,
  reportType: PropTypes.any,
  period: PropTypes.any,
  comparePeriod: PropTypes.any,
};
